import { type RefObject, useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * For sheets and drawers: puts focus inside when opened (first in `startIn`, if given), keeps Tab
 * inside, closes on Escape, and gives focus back to whatever opened it.
 */
export function useModalFocus(
  open: boolean,
  panel: RefObject<HTMLElement | null>,
  onClose: () => void,
  startIn?: string,
) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const node = panel.current;
    const preferred = startIn
      ? FOCUSABLE.split(', ')
          .map((selector) => `${startIn} ${selector}`)
          .join(', ')
      : null;
    const first =
      (preferred ? node?.querySelector<HTMLElement>(preferred) : null) ?? node?.querySelector<HTMLElement>(FOCUSABLE);
    // Without scrolling: focusing can scroll even containers that hide their overflow, shifting the page.
    first?.focus({ preventScroll: true });

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !node) return;
      const items = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (!items.length) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem?.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // The chat may have moved on while the sheet was open: giving focus back mustn't scroll to the opener.
      opener?.focus?.({ preventScroll: true });
    };
  }, [open, panel, startIn]);
}
